import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { OtpInput } from '@/components/ui/OtpInput'
import { getCountriesApi, sendPhoneOtpApi, verifyPhoneOtpApi, registerPhoneApi, type Country } from '@/lib/api'
import { toast } from 'sonner'
import { ArrowLeftIcon, ArrowRightIcon, SmartphoneIcon, SendIcon, ShieldCheckIcon } from 'lucide-react'

type Step = 'phone' | 'otp' | 'register'

interface PhoneLoginPanelProps {
  /** Called with the JWT + full API response after a successful login/registration. */
  onAuthenticated: (token: string, data: Record<string, unknown>) => Promise<void>
}

const ROLE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'company_admin', label: 'Company administrator' },
]

/** Build an E.164 number from a national number + dialing code (e.g. +234…). */
function toE164(national: string, dialingCode: string): string {
  const digits = national.replace(/\D/g, '')
  return `+${dialingCode.replace(/\D/g, '')}${digits}`
}

export function PhoneLoginPanel({ onAuthenticated }: PhoneLoginPanelProps) {
  const [step, setStep] = useState<Step>('phone')
  const [countries, setCountries] = useState<Country[]>([])
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const [phone, setPhone] = useState('')
  const [maskedPhone, setMaskedPhone] = useState('')
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''))
  const [resendAfter, setResendAfter] = useState(0)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [role, setRole] = useState('employee')
  const [loading, setLoading] = useState(false)
  const [loadingCountries, setLoadingCountries] = useState(true)

  useEffect(() => {
    let cancelled = false
    getCountriesApi()
      .then(({ data }) => {
        if (cancelled) return
        // Only countries with an ISO code + dialing code are usable for SMS.
        const usable = data.filter((c) => c.iso_2 && c.dialing_code)
        setCountries(usable)
        setSelectedCountry(usable[0] ?? null)
      })
      .catch(() => toast.error('Could not load country list'))
      .finally(() => {
        if (!cancelled) setLoadingCountries(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Resend countdown
  useEffect(() => {
    if (resendAfter <= 0) return
    const t = setInterval(() => setResendAfter((s) => Math.max(0, s - 1)), 1000)
    return () => clearInterval(t)
  }, [resendAfter])

  const nationalDigits = phone.replace(/\D/g, '')

  const handleSendOtp = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!selectedCountry) {
      toast.error('Please select a country')
      return
    }
    if (nationalDigits.length < 5) {
      toast.error('Enter a valid phone number')
      return
    }
    setLoading(true)
    try {
      const res = await sendPhoneOtpApi(nationalDigits, selectedCountry.iso_2!)
      setMaskedPhone(res.data.phone_number_masked)
      setResendAfter(res.data.resend_after ?? 60)
      setOtp(Array(6).fill(''))
      setStep('otp')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send code'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async () => {
    if (otp.some((d) => d === '')) {
      toast.error('Enter the 6-digit code')
      return
    }
    if (!selectedCountry) return
    setLoading(true)
    try {
      const data = await verifyPhoneOtpApi(toE164(nationalDigits, selectedCountry.dialing_code!), otp.join(''))
      const token = extractToken(data)
      if (!token) throw new Error('No token received from server')
      await onAuthenticated(token, data)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) {
        // Phone verified but no account — move to registration (OTP stays valid).
        setStep('register')
        toast.info('No account found for this number — create one below')
      } else {
        toast.error(err instanceof Error ? err.message : 'Verification failed')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Enter your first and last name')
      return
    }
    if (!selectedCountry) return
    setLoading(true)
    try {
      const data = await registerPhoneApi({
        phone_number: nationalDigits,
        country_code: selectedCountry.iso_2!,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        role,
        otp_code: otp.join(''),
      })
      const token = extractToken(data)
      if (!token) throw new Error('No token received from server')
      await onAuthenticated(token, data)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  if (loadingCountries) {
    return <p className="text-center text-sm text-white/40 py-8">Loading countries…</p>
  }

  return (
    <div className="flex flex-col gap-5">
      {step === 'phone' && (
        <form onSubmit={handleSendOtp} className="flex flex-col gap-5" aria-label="Phone login">
          <div className="flex flex-col gap-2">
            <Label htmlFor="phone-country" className="text-sm font-medium text-white/70">
              Country
            </Label>
            <select
              id="phone-country"
              value={selectedCountry?.iso_2 ?? ''}
              onChange={(e) => setSelectedCountry(countries.find((c) => c.iso_2 === e.target.value) ?? null)}
              className="h-10 rounded-md border border-white/15 bg-[#0B1120] text-white text-sm px-3 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 focus-visible:outline-none"
            >
              {countries.map((c) => (
                <option key={c.iso_2} value={c.iso_2 ?? ''}>
                  {c.country} {c.dialing_code}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="phone-number" className="text-sm font-medium text-white/70">
              Phone number
            </Label>
            <div className="flex items-center gap-2">
              <span className="shrink-0 rounded-md border border-white/15 bg-white/[0.04] px-3 h-10 inline-flex items-center text-sm text-white/60">
                {selectedCountry?.dialing_code ?? '+'}
              </span>
              <Input
                id="phone-number"
                type="tel"
                inputMode="tel"
                placeholder="801 234 5678"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d\s()+-]/g, ''))}
                autoComplete="tel"
                className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 mt-2 shadow-xl shadow-[#00E5A0]/25 disabled:opacity-60"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="size-4 rounded-full border-2 border-[#0B1120]/30 border-t-[#0B1120] animate-spin" />
                Sending…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <SendIcon className="size-4" />
                Send code
              </span>
            )}
          </Button>
        </form>
      )}

      {step === 'otp' && (
        <div className="flex flex-col gap-5">
          <div className="rounded-xl border border-[#00E5A0]/20 bg-[#00E5A0]/[0.06] p-4">
            <div className="flex items-center gap-2">
              <SmartphoneIcon className="size-4 text-[#00E5A0]" />
              <p className="text-sm font-medium text-white/80">Enter your verification code</p>
            </div>
            <p className="text-xs text-white/50 mt-1">
              Sent to {maskedPhone}. Code expires in 5 minutes.
            </p>
          </div>

          <OtpInput
            value={otp}
            onChange={setOtp}
            autoFocusFirst
            inputClassName="size-11 rounded-lg border border-white/15 bg-white/[0.04] text-center text-lg font-semibold text-white focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 focus-visible:outline-none"
          />

          <Button
            type="button"
            onClick={handleVerify}
            disabled={loading || otp.some((d) => d === '')}
            className="w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 shadow-xl shadow-[#00E5A0]/25 disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="size-4 rounded-full border-2 border-[#0B1120]/30 border-t-[#0B1120] animate-spin" />
                Verifying…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Verify &amp; continue
                <ArrowRightIcon className="size-4" />
              </span>
            )}
          </Button>

          <div className="flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => setStep('phone')}
              className="flex items-center gap-1 text-white/40 hover:text-white/70 transition-colors"
            >
              <ArrowLeftIcon className="size-3" />
              Wrong number?
            </button>
            {resendAfter > 0 ? (
              <span className="text-white/30">Resend code in 0:{String(resendAfter).padStart(2, '0')}</span>
            ) : (
              <button
                type="button"
                onClick={() => void handleSendOtp()}
                className="text-[#00E5A0] hover:text-[#00E5A0]/80 font-medium transition-colors"
              >
                Resend code
              </button>
            )}
          </div>
        </div>
      )}

      {step === 'register' && (
        <form onSubmit={handleRegister} className="flex flex-col gap-5" aria-label="Create account with phone">
          <div className="rounded-xl border border-[#00E5A0]/20 bg-[#00E5A0]/[0.06] p-4">
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="size-4 text-[#00E5A0]" />
              <p className="text-sm font-medium text-white/80">No account found for {maskedPhone}</p>
            </div>
            <p className="text-xs text-white/50 mt-1">
              Your number is verified — finish creating your account.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="phone-first-name" className="text-sm font-medium text-white/70">
                First name
              </Label>
              <Input
                id="phone-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="phone-last-name" className="text-sm font-medium text-white/70">
                Last name
              </Label>
              <Input
                id="phone-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="phone-role" className="text-sm font-medium text-white/70">
              I am a…
            </Label>
            <select
              id="phone-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="h-10 rounded-md border border-white/15 bg-[#0B1120] text-white text-sm px-3 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 focus-visible:outline-none"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 mt-2 shadow-xl shadow-[#00E5A0]/25 disabled:opacity-60"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="size-4 rounded-full border-2 border-[#0B1120]/30 border-t-[#0B1120] animate-spin" />
                Creating account…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Create account
                <ArrowRightIcon className="size-4" />
              </span>
            )}
          </Button>
        </form>
      )}
    </div>
  )
}

/** Pull the JWT out of the common response shapes (top-level or data.token). */
function extractToken(data: Record<string, unknown>): string | undefined {
  const sources: Array<Record<string, unknown> | undefined> = [data, data.data as Record<string, unknown> | undefined]
  for (const src of sources) {
    const val = src?.token
    if (typeof val === 'string' && val.length > 0) return val
  }
  return undefined
}
